'use client'

import RankBottomCta from './RankBottomCta'
import RankEmpty from './RankEmpty'
import RankHeader from './RankHeader'
import RankInAppTopBar from './RankInAppTopBar'
import RankList from './RankList'
import RankLnb from './RankLnb'
import RankSelectSheet from './RankSelectSheet'
import RankShareCard from './RankShareCard'
import RankSummaryCard from './RankSummaryCard'
import RankTabs from './RankTabs'
import {
  getRankReferenceNote,
  RANK_SEGMENTS,
  RankTrackingProvider,
  useRankingPage,
} from '../_features/rank'

import type { IRankingList, TRankFuelType, TRankTab } from '../_features/rank'
import type { CSSProperties } from 'react'

/** 하단 CTA 높이 측정 전 임시 여백(px) — 실측(≈143~안전영역 포함)보다 크게 잡아 초기 가림 방지 */
const BOTTOM_CTA_FALLBACK_HEIGHT_PX = 160
// 랭킹 더보기 CTA 버튼 문구 — 10위 초과(전체보기) / 10개 이하(편하게 보기) 분기 (Figma 9241:28086)
const DESKTOP_MORE_TEXT = '마이클에서 전체보기'
const MOBILE_MORE_TEXT = '앱에서 전체보기'
// 10개 이하 CTA는 데스크탑·모바일 모두 '앱에서 편하게 보기'로 통일
const DESKTOP_EASY_TEXT = '앱에서 편하게 보기'
const MOBILE_EASY_TEXT = '앱에서 편하게 보기'

interface IRankingPageProps {
  ranking: IRankingList
  activeTab: TRankTab
  activeSegment: string
  fuelType: TRankFuelType
  /** 세그먼트에 랭킹 데이터가 있는 유종 (빈 배열이면 유종 버튼 숨김) */
  fuelOptions: TRankFuelType[]
}

/**
 * 모두의 차고 랭킹 페이지 오케스트레이터 (뷰).
 * - 탭/유종/세그먼트 변경 → URL 쿼리 갱신(soft navigation)으로 서버 재조회(ISR).
 * - 데스크탑: 좌측 LNB(차급) + 본문 / 모바일·인앱: 단일 컬럼(세그먼트는 히어로 드롭다운).
 * 상태·라우팅·트래킹 로직은 useRankingPage 훅이 담당한다.
 */
const RankingPage = ({
  ranking,
  activeTab,
  activeSegment,
  fuelType,
  fuelOptions,
}: IRankingPageProps) => {
  const {
    isInApp,
    selection,
    isElectric,
    isFuelCost,
    rankTrack,
    bottomCtaRef,
    bottomCtaVisible,
    bottomCtaHeight,
    title,
    description,
    heroImageUrls,
    segmentOptions,
    shareTitle,
    shareImageUrl,
    isEmpty,
    hasMore,
    goToAppMobile,
    goToAppDesktop,
    lockAbAfSub1,
    handleTabSelect,
    handleSegmentSelect,
    handleFuelChange,
    handleFuelTriggerClick,
    handleEmptyAction,
  } = useRankingPage({ ranking, activeTab, activeSegment, fuelType })

  const hideFuelSelector = isFuelCost || fuelOptions.length === 0

  const renderFuelSelector = () => (
    <RankSelectSheet
      triggerVariant='chip'
      value={selection.fuelType}
      options={fuelOptions}
      onChange={handleFuelChange}
      onTriggerClick={handleFuelTriggerClick}
      title='유종 선택'
    />
  )

  const renderList = (moreButtonText: string, onMoreClick: () => void) => (
    <RankList
      items={isInApp ? ranking.allItems : ranking.items}
      previewItems={ranking.previewItems}
      tab={selection.tab}
      segment={selection.segment}
      totalCount={ranking.totalCount}
      moreButtonText={moreButtonText}
      onMoreClick={onMoreClick}
      showChevron={isInApp}
      showMoreCta={!isInApp}
    />
  )

  return (
    <RankTrackingProvider value={rankTrack}>
      <div
        className='min-h-screen bg-background-surface-default lg:bg-background-surface-basement'
        style={
          isInApp ? ({ '--rank-tab-top': '56px' } as CSSProperties) : undefined
        }
      >
        {/* GNB·탭은 전체 폭(해상도 full)으로 배경을 깔고, 본문만 max-w로 가둔다 (WEB-4616) */}
        {isInApp ? <RankInAppTopBar /> : <RankHeader />}
        <RankTabs
          activeTab={selection.tab}
          isElectric={isElectric}
          onSelect={handleTabSelect}
        />
        <div
          className={`mx-auto flex w-full max-w-[1024px] flex-col bg-background-surface-default lg:bg-transparent ${
            isFuelCost ? 'lg:max-w-[680px]' : ''
          }`}
        >
          {/* 모바일 / 인앱 */}
          <div className='lg:hidden'>
            <RankSummaryCard
              variant='mobile'
              segment={selection.segment}
              title={title}
              referenceNote={getRankReferenceNote({
                tab: selection.tab,
                variant: 'mobile',
                ranking,
              })}
              heroImageUrls={heroImageUrls}
              hasRanking={!isEmpty}
              showSegment={!isFuelCost}
              fuelSelector={hideFuelSelector ? undefined : renderFuelSelector()}
              segmentSelector={
                isFuelCost ? undefined : (
                  <div className='shrink-0'>
                    <RankSelectSheet
                      triggerVariant='underline'
                      value={selection.segment}
                      options={segmentOptions}
                      onChange={handleSegmentSelect}
                      title='차급 선택'
                    />
                  </div>
                )
              }
            />
            <div
              aria-hidden
              className='h-[10px] w-full bg-background-neutral-weak'
            />
            {isEmpty ? (
              <RankEmpty onAction={handleEmptyAction} />
            ) : (
              <>
                {renderList(
                  hasMore ? MOBILE_MORE_TEXT : MOBILE_EASY_TEXT,
                  goToAppMobile
                )}
                <div
                  aria-hidden
                  className='h-[10px] w-full bg-background-neutral-weak'
                />
                <RankShareCard
                  title={shareTitle}
                  imageUrl={shareImageUrl}
                  isInApp={isInApp}
                />
              </>
            )}
            {/* 하단 고정 CTA 영역 확보 — 실제 CTA 높이만큼 예약해 공유 카드가 가리지 않게 한다.
              (측정 전 초기값 폴백, 인앱은 앱이 자체 CTA 제공) */}
            {isInApp ? null : (
              <div
                aria-hidden
                style={{
                  height: bottomCtaHeight || BOTTOM_CTA_FALLBACK_HEIGHT_PX,
                }}
              />
            )}
          </div>

          {/* 데스크탑 */}
          <div className='hidden gap-dimension-x6 px-dimension-x5 py-dimension-x6 lg:flex'>
            {isFuelCost ? null : (
              <RankLnb
                segments={RANK_SEGMENTS}
                activeSegment={selection.segment}
                onSelect={handleSegmentSelect}
              />
            )}
            <div className='min-w-px flex-1'>
              <RankSummaryCard
                variant='desktop'
                segment={selection.segment}
                title={title}
                description={description}
                referenceNote={getRankReferenceNote({
                  tab: selection.tab,
                  variant: 'desktop',
                  ranking,
                })}
                heroImageUrls={heroImageUrls}
                showSegment={!isFuelCost}
                fuelSelector={
                  hideFuelSelector ? undefined : renderFuelSelector()
                }
              />
              {isEmpty ? (
                <div className='pt-dimension-x2'>
                  <RankEmpty onAction={handleEmptyAction} />
                </div>
              ) : (
                <>
                  <div className='pt-dimension-x2'>
                    {renderList(
                      hasMore ? DESKTOP_MORE_TEXT : DESKTOP_EASY_TEXT,
                      goToAppDesktop
                    )}
                  </div>
                  <RankShareCard
                    title={shareTitle}
                    imageUrl={shareImageUrl}
                    isInApp={isInApp}
                  />
                </>
              )}
            </div>
          </div>
          <div aria-hidden className='h-px w-full' />
        </div>

        {isInApp ? null : (
          <RankBottomCta
            ref={bottomCtaRef}
            totalCount={ranking.totalCount}
            visible={bottomCtaVisible}
            afSub1={lockAbAfSub1}
          />
        )}
      </div>
    </RankTrackingProvider>
  )
}

export default RankingPage
